import React, { useState, useEffect, useRef } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { getPageImageUrl, getPageThumbnailUrl } from '../api/client';

interface DocumentImagePortalProps {
  documentId: number;
  allowedPages: number[];
  initialPage: number;
  documentName: string;
  onClose: () => void;
  onPdfFallback?: () => void;
}

export function DocumentImagePortal({
  documentId,
  allowedPages,
  initialPage,
  documentName,
  onClose,
  onPdfFallback
}: DocumentImagePortalProps) {
  const transformRef = useRef<React.ElementRef<typeof TransformWrapper>>(null);
  const thumbnailStripRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [displayScale, setDisplayScale] = useState<number>(1);
  const [loadedPages, setLoadedPages] = useState<Set<number>>(new Set([initialPage]));

  // Responsive layout detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Max zoom from settings (default 2000%)
  const maxZoom = (() => {
    const saved = localStorage.getItem('documentViewer_maxZoom');
    return saved ? parseInt(saved, 10) / 100 : 20;
  })();

  // Progressive loading: load pages sequentially
  useEffect(() => {
    const loadSequentially = async () => {
      await loadPage(initialPage);
      const otherPages = allowedPages.filter(p => p !== initialPage);
      for (const page of otherPages) {
        await new Promise(resolve => setTimeout(resolve, 100));
        await loadPage(page);
      }
    };
    loadSequentially();
  }, []);

  const loadPage = (pageNum: number): Promise<void> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        setLoadedPages(prev => {
          const newSet = new Set(prev);
          newSet.add(pageNum);
          return newSet;
        });
        resolve();
      };
      img.onerror = () => {
        setLoadedPages(prev => {
          const newSet = new Set(prev);
          newSet.add(pageNum);
          return newSet;
        });
        resolve();
      };
      img.src = getPageImageUrl(documentId, pageNum);
    });
  };

  // Auto-scroll thumbnail strip to current page
  useEffect(() => {
    if (thumbnailStripRef.current) {
      const strip = thumbnailStripRef.current;
      const currentThumb = strip.children[currentPage - 1] as HTMLElement;
      if (currentThumb) {
        currentThumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }
  }, [currentPage]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        const currentIndex = allowedPages.indexOf(currentPage);
        if (currentIndex < allowedPages.length - 1) {
          setCurrentPage(allowedPages[currentIndex + 1]);
        }
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        const currentIndex = allowedPages.indexOf(currentPage);
        if (currentIndex > 0) {
          setCurrentPage(allowedPages[currentIndex - 1]);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, allowedPages, onClose]);

  // Browser back button handling
  useEffect(() => {
    window.history.pushState({ modal: 'documentImagePortal' }, '');
    const handlePopState = () => {
      onClose();
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (window.history.state?.modal === 'documentImagePortal') {
        window.history.back();
      }
    };
  }, [onClose]);

  const handleThumbnailClick = (pageNum: number) => {
    setCurrentPage(pageNum);
    const pageEl = document.getElementById(`page-${pageNum}`);
    if (pageEl) {
      transformRef.current?.zoomToElement(pageEl, 1, 300);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: '#1a1a1a',
        zIndex: 2000,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header */}
      <div style={{
        padding: isMobile ? '8px 12px' : '12px 16px',
        background: '#222',
        color: '#fff',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        gap: isMobile ? '8px' : '16px',
        flexShrink: 0,
        borderBottom: '1px solid #333'
      }}>
        {isMobile ? (
          <>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <button
                onClick={onClose}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '16px',
                  padding: '4px 8px'
                }}
              >
                ← Back
              </button>
              <span style={{ fontSize: '12px', color: '#888' }}>
                Page {currentPage} of {allowedPages.length}
              </span>
            </div>
            <div style={{
              fontSize: '14px',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              marginBottom: '4px'
            }}>
              {documentName}
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center' }}>
              <button
                onClick={() => transformRef.current?.zoomOut()}
                style={{
                  padding: '6px 12px',
                  background: '#444',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                −
              </button>
              <span style={{ minWidth: '50px', textAlign: 'center', fontSize: '14px' }}>
                {Math.round(displayScale * 100)}%
              </span>
              <button
                onClick={() => transformRef.current?.zoomIn()}
                style={{
                  padding: '6px 12px',
                  background: '#444',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                +
              </button>
              <button
                onClick={() => transformRef.current?.resetTransform()}
                style={{
                  padding: '6px 12px',
                  background: '#0066cc',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Fit
              </button>
              {onPdfFallback && (
                <button
                  onClick={onPdfFallback}
                  style={{
                    padding: '6px 12px',
                    background: '#666',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  PDF
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '16px',
                padding: '4px 8px'
              }}
            >
              ← Back
            </button>
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <div style={{
                fontSize: '14px',
                fontWeight: 500,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {documentName}
              </div>
              <div style={{ fontSize: '12px', color: '#888' }}>
                Page {currentPage} of {allowedPages.length}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                onClick={() => transformRef.current?.zoomOut()}
                style={{
                  padding: '6px 12px',
                  background: '#444',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                −
              </button>
              <span style={{ minWidth: '50px', textAlign: 'center', fontSize: '14px' }}>
                {Math.round(displayScale * 100)}%
              </span>
              <button
                onClick={() => transformRef.current?.zoomIn()}
                style={{
                  padding: '6px 12px',
                  background: '#444',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                +
              </button>
              <button
                onClick={() => transformRef.current?.resetTransform()}
                style={{
                  padding: '6px 12px',
                  background: '#0066cc',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Fit
              </button>
              {onPdfFallback && (
                <button
                  onClick={onPdfFallback}
                  style={{
                    padding: '6px 12px',
                    background: '#666',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    marginLeft: '8px'
                  }}
                >
                  PDF
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Main Image Viewer */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <TransformWrapper
          ref={transformRef}
          initialScale={1}
          minScale={0.1}
          maxScale={maxZoom}
          limitToBounds={false}
          centerZoomedOut={true}
          doubleClick={{ mode: 'reset' }}
          onTransformed={(_ref, state) => setDisplayScale(state.scale)}
        >
          <TransformComponent
            wrapperStyle={{
              width: '100%',
              height: '100%',
              background: '#1a1a1a',
              cursor: 'grab'
            }}
            contentStyle={{
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
              padding: '16px'
            }}
          >
            {allowedPages.map((pageNum, index) => (
              <div
                key={pageNum}
                id={`page-${pageNum}`}
                style={{
                  width: '100%',
                  maxWidth: '1200px',
                  position: 'relative',
                  background: '#fff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                }}
              >
                {!loadedPages.has(pageNum) ? (
                  <div style={{
                    width: '100%',
                    aspectRatio: '8.5/11',
                    background: '#333',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#666'
                  }}>
                    Loading...
                  </div>
                ) : (
                  <img
                    src={getPageImageUrl(documentId, pageNum)}
                    alt={`Page ${pageNum}`}
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block'
                    }}
                    loading={index === 0 ? 'eager' : 'lazy'}
                  />
                )}
              </div>
            ))}
          </TransformComponent>
        </TransformWrapper>
      </div>

      {/* Thumbnail Strip */}
      {allowedPages.length > 1 && (
        <div
          ref={thumbnailStripRef}
          style={{
            height: '80px',
            background: '#222',
            borderTop: '1px solid #333',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px',
            overflowX: 'auto',
            overflowY: 'hidden',
            flexShrink: 0
          }}
        >
          {allowedPages.map((pageNum) => (
            <button
              key={pageNum}
              onClick={() => handleThumbnailClick(pageNum)}
              style={{
                flexShrink: 0,
                width: '60px',
                height: '60px',
                padding: 0,
                border: currentPage === pageNum ? '2px solid #0066cc' : '2px solid transparent',
                borderRadius: '4px',
                overflow: 'hidden',
                background: '#333',
                cursor: 'pointer',
                position: 'relative'
              }}
            >
              <img
                src={getPageThumbnailUrl(documentId, pageNum)}
                alt={`Page ${pageNum}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
                loading="lazy"
              />
              <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                background: 'rgba(0,0,0,0.7)',
                color: '#fff',
                fontSize: '10px',
                padding: '2px',
                textAlign: 'center'
              }}>
                {pageNum}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
